import { Package, Plus } from 'lucide-react';
import { ItemRow } from '@/entities/item/components/item-row';
import type { Item } from '@/entities/item/schemas/item';
import { RecordEditor } from '@/entities/shared/record/record-editor';
import { useEditorParam } from '@/shared/components/editor-sheet/use-editor-param';
import { EmptyState } from '@/shared/components/empty-state';
import { ListCard } from '@/shared/components/list/list-card';
import { ListSkeleton } from '@/shared/components/list/list-skeleton';
import { Page } from '@/shared/components/page/page';
import { PageHeader } from '@/shared/components/page/page-header';
import { useTranslation } from '@/shared/i18n/use-translation';
import { formatCents } from '@/shared/lib/money/format-cents';
import { Button } from '@/shared/ui/button/button';
import { useInventory } from '../hooks/use-inventory';
import { InventoryToolbar } from './inventory-toolbar';
import { ItemEditor } from './item-editor';

export function InventoryScreen() {
  const { t, tn } = useTranslation();
  const inventory = useInventory();
  const editor = useEditorParam();
  const { items, shown } = inventory;
  const count = items?.length ?? 0;
  return (
    <Page>
      <PageHeader
        title={t('Inventory')}
        description={
          !items
            ? undefined
            : inventory.total > 0
              ? tn(count, '{count} product · {total} invested', '{count} products · {total} invested', {
                  total: formatCents(inventory.total),
                })
              : tn(count, '{count} product', '{count} products')
        }
        primaryAction={{ label: t('Add'), onClick: () => editor.open('new') }}
      />
      <InventoryToolbar inventory={inventory} />
      {!shown ? (
        <ListSkeleton />
      ) : count === 0 ? (
        <EmptyState
          icon={Package}
          title={t('The inventory is empty')}
          description={t(
            'Add what you already have: water, food, first aid kit, torches… with its price and expiry date.',
          )}
          action={
            <Button onClick={() => editor.open('new')}>
              <Plus /> {t('Add the first product')}
            </Button>
          }
        />
      ) : shown.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">{t('Nothing matches the filter.')}</p>
      ) : (
        <ListCard>
          {shown.map((view) => (
            <ItemRow key={view.id} view={view} onOpen={() => editor.open(view.id)} />
          ))}
        </ListCard>
      )}
      <RecordEditor<Item> collection="items" id={editor.editing}>
        {(view) => <ItemEditor view={view} items={items ?? []} onClose={editor.close} />}
      </RecordEditor>
    </Page>
  );
}
